/**
 * Query builders (Drizzle) para el vault. Devuelven queries compilables que el
 * web envuelve en `createPowerSyncQuery` para reactividad local-first.
 */

import {
  and,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  vault,
  vault_account,
  vault_attachment,
  vault_item,
  vault_membership,
  vault_share,
} from "@boveda/powersync";

/** Fila raíz de cuenta del usuario (0 o 1 fila). */
export function getVaultAccountQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(vault_account).where(eq(vault_account.user_id, userId)).limit(1);
}

/** Vault(s) personales del usuario, no borrados. */
export function getPersonalVaultsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(vault)
    .where(and(eq(vault.owner_user_id, userId), isNull(vault.deleted_at)))
    .orderBy(desc(vault.created_at));
}

/** Items vivos (no borrados) de un vault, más recientes primero. */
export function getVaultItemsQuery(db: PowerSyncDrizzleDB, vaultId: string) {
  return db
    .select()
    .from(vault_item)
    .where(and(eq(vault_item.vault_id, vaultId), isNull(vault_item.deleted_at)))
    .orderBy(desc(vault_item.updated_at));
}

/**
 * Vaults de ORGANIZACIÓN visibles para este cliente (el sync ya los limita a
 * las orgs del usuario), no borrados.
 */
export function getOrgVaultsQuery(db: PowerSyncDrizzleDB) {
  return db
    .select()
    .from(vault)
    .where(and(eq(vault.type, "organization"), isNull(vault.deleted_at)))
    .orderBy(desc(vault.created_at));
}

/** TODAS mis membresías de vaults de organización (para reactividad de lista). */
export function getMyVaultMembershipsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(vault_membership).where(eq(vault_membership.user_id, userId));
}

/** MI membresía (clave sellada) de un vault de organización (0 o 1 fila). */
export function getMyVaultMembershipQuery(db: PowerSyncDrizzleDB, vaultId: string, userId: string) {
  return db
    .select()
    .from(vault_membership)
    .where(and(eq(vault_membership.vault_id, vaultId), eq(vault_membership.user_id, userId)))
    .limit(1);
}

/** Secretos compartidos CONMIGO (sobres sellados a mi clave pública). */
export function getIncomingSharesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(vault_share)
    .where(eq(vault_share.to_user_id, userId))
    .orderBy(desc(vault_share.created_at));
}

/** Adjuntos vivos (no borrados) de un item, más recientes primero. */
export function getItemAttachmentsQuery(db: PowerSyncDrizzleDB, itemId: string) {
  return db
    .select()
    .from(vault_attachment)
    .where(and(eq(vault_attachment.item_id, itemId), isNull(vault_attachment.deleted_at)))
    .orderBy(desc(vault_attachment.created_at));
}

/** Items soft-borrados de un vault (papelera). */
export function getVaultTrashQuery(db: PowerSyncDrizzleDB, vaultId: string) {
  return db
    .select()
    .from(vault_item)
    .where(eq(vault_item.vault_id, vaultId))
    .orderBy(desc(vault_item.updated_at));
}
